/**
 * New Task page — matches wireframe #2.
 * Underlined display-style fields (same aesthetic as AddStatement).
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Loader2, ChevronDown, Upload, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;

const HeadlineSelect = ({ testid, value, onValueChange, options, placeholder = 'Choose' }) => {
  const norm = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        data-testid={testid}
        className="h-auto w-full bg-transparent border-0 border-b-2 border-ink/15 rounded-none px-0 pb-2 pt-0 font-display text-5xl font-bold text-ink tracking-tight focus:ring-0 focus:border-primary hover:border-ink/40 transition-colors flex items-center justify-between gap-3 [&>span]:!line-clamp-none [&>span]:truncate [&>svg]:hidden"
      >
        <SelectValue placeholder={placeholder} />
        <span className="inline-flex shrink-0">
          <ChevronDown className="w-7 h-7 text-ink/70" />
        </span>
      </SelectTrigger>
      <SelectContent>
        {norm.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const NewTask = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignTo, setAssignTo] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/tasks/assignable-users`)
      .then((r) => setAssignableUsers(r.data.users || []))
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You don't have permission to assign tasks");
          navigate('/tasks');
        } else {
          toast.error('Failed to load assignable users');
        }
      });
  }, [navigate]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error('File exceeds 10 MB');
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!assignTo) { toast.error('Choose an assignee'); return; }
    if (!title.trim()) { toast.error('What needs to be done?'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('assigned_to', assignTo);
      if (description.trim()) fd.append('description', description.trim());
      if (dueDate) fd.append('due_date', dueDate);
      if (file) fd.append('file', file);
      await axios.post(`${API}/tasks`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Task assigned');
      navigate('/tasks');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="new-task-page">
      <div className="flex items-start justify-between mb-12 gap-4">
        <div className="flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-ink/5 text-ink/70"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-display text-4xl font-bold text-ink tracking-tight">New Task</h1>
            <p className="text-ink/60 mt-1">Delegate to someone on your team</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
        <div>
          <label className="text-sm font-medium text-ink/70 mb-2 block">Assign To</label>
          <HeadlineSelect
            testid="select-assignee"
            value={assignTo}
            onValueChange={setAssignTo}
            options={assignableUsers.map((u) => ({ value: u.id, label: u.name }))}
            placeholder="Choose"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-ink/70 mb-2 block">When to complete</label>
          <div className="flex gap-2 mb-3">
            {(() => {
              const todayIso = new Date().toISOString().slice(0, 10);
              const tomorrowIso = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
              const PRESETS = [
                { label: 'Today', value: todayIso },
                { label: 'Tomorrow', value: tomorrowIso },
              ];
              return PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  data-testid={`due-preset-${p.label.toLowerCase()}`}
                  onClick={() => setDueDate(p.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
                    dueDate === p.value
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-ink/70 border-mute/30 hover:bg-surface-100 hover:text-ink'
                  }`}
                >
                  {p.label}
                </button>
              ));
            })()}
          </div>
          <input
            data-testid="input-due-date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 font-display text-3xl font-bold text-ink tracking-tight transition-colors"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-ink/70 mb-2 block">Upload File (any)</label>
          {file ? (
            <div className="flex items-center justify-between border-b-2 border-primary pb-2 font-display text-2xl font-bold text-ink tracking-tight">
              <span className="truncate">{file.name}</span>
              <button
                data-testid="clear-file"
                onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                className="p-1 rounded-full hover:bg-ink/5 text-ink/60"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              data-testid="upload-file-btn"
              onClick={() => fileInputRef.current?.click()}
              className="w-full text-left bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 font-display text-2xl font-bold text-ink/30 tracking-tight hover:text-ink/60 hover:border-ink/40 transition-colors inline-flex items-center gap-2"
            >
              <Upload className="w-5 h-5" /> Choose
            </button>
          )}
          <input ref={fileInputRef} type="file" hidden onChange={handleFile} data-testid="file-input" />
          <p className="text-xs text-ink/50 mt-1">Max 10 MB</p>
        </div>
      </div>

      <div className="mt-16">
        <label className="text-sm font-medium text-ink/70 mb-2 block">What to do</label>
        <input
          data-testid="input-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Prepare sop urgently"
          className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 font-display text-4xl font-bold text-ink tracking-tight placeholder:text-ink/25 transition-colors"
        />
      </div>

      <div className="mt-12">
        <label className="text-sm font-medium text-ink/70 mb-2 block">
          Description <span className="text-ink/40 font-normal">(optional)</span>
        </label>
        <textarea
          data-testid="input-description"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add context, links, or step-by-step details…"
          className="w-full bg-transparent border-b-2 border-ink/15 focus:border-primary focus:outline-none pb-2 text-base text-ink placeholder:text-ink/25 transition-colors resize-none"
        />
      </div>

      <div className="mt-16">
        <button
          data-testid="assign-task-btn"
          onClick={handleSubmit}
          disabled={saving}
          className="h-14 px-12 rounded-full bg-primary text-white font-display text-xl font-semibold tracking-tight shadow-lg shadow-primary/20 hover:bg-primary-hover transition-all disabled:opacity-60 inline-flex items-center gap-2"
        >
          {saving && <Loader2 className="w-5 h-5 animate-spin" />}
          Assign Task
        </button>
      </div>
    </div>
  );
};

export default NewTask;
